import React from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Button,
  Grid
} from '@mui/material';
import { FlashOn, BatteryChargingFull, Speed } from '@mui/icons-material';
import { motion } from 'framer-motion';

const ChargingStrategy = ({ selectedStrategy, onStrategyChange, isCalculating }) => {
  const strategies = [
    {
      id: 'fastest',
      name: 'Few Long',
      description: '50-150kW',
      subtitle: 'Optimal balance for fastest trip',
      color: '#f59e0b',
      icon: <Speed sx={{ fontSize: 20 }} />
    },
    {
      id: 'quickest',
      name: 'Quick Many',
      description: '30-100kW',
      subtitle: 'More stops, quick charges',
      color: '#4caf50',
      icon: <FlashOn sx={{ fontSize: 20 }} />
    },
    {
      id: 'economical',
      name: 'Many Short',
      description: 'All speeds',
      subtitle: 'Most economical option',
      color: '#3b82f6',
      icon: <BatteryChargingFull sx={{ fontSize: 20 }} />
    }
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <FlashOn sx={{ color: '#4caf50', fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#333' }}>
          Charging Strategy
        </Typography>
      </Box>
      
      {/* Button Grid Style Selection - matching the image */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          borderRadius: 2, 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #e0e0e0' 
        }}
      >
        <Grid container spacing={1}>
          {strategies.map((strategy) => (
            <Grid item xs={4} key={strategy.id}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  fullWidth
                  variant={selectedStrategy === strategy.id ? 'contained' : 'outlined'}
                  onClick={() => onStrategyChange(strategy.id)}
                  disabled={isCalculating}
                  sx={{
                    p: 2,
                    height: 80,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: selectedStrategy === strategy.id ? strategy.color : '#e0e0e0',
                    backgroundColor: selectedStrategy === strategy.id ? strategy.color : 'white',
                    color: selectedStrategy === strategy.id ? 'white' : '#333',
                    flexDirection: 'column',
                    gap: 0.5,
                    textTransform: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: strategy.color,
                      backgroundColor: selectedStrategy === strategy.id ? strategy.color : `${strategy.color}10`,
                      color: selectedStrategy === strategy.id ? 'white' : strategy.color
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6
                    }
                  }}
                >
                  <Box sx={{ 
                    color: selectedStrategy === strategy.id ? 'white' : strategy.color,
                    mb: 0.5
                  }}>
                    {strategy.icon}
                  </Box>
                  <Typography 
                    variant="caption" 
                    fontWeight={600}
                    sx={{ 
                      lineHeight: 1,
                      color: selectedStrategy === strategy.id ? 'white' : '#333'
                    }}
                  >
                    {strategy.name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.7rem',
                      color: selectedStrategy === strategy.id ? 'rgba(255,255,255,0.8)' : '#666',
                      lineHeight: 1
                    }}
                  >
                    {strategy.description}
                  </Typography>
                </Button>
              </motion.div>
            </Grid>
          ))}
        </Grid>
        
        {/* Description Text */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mt: 1.5,
            fontSize: '0.75rem'
          }}
        >
          {selectedStrategy === 'fastest' && 'Optimal balance for fastest trip'}
          {selectedStrategy === 'quickest' && 'More stops, quick charges'}
          {selectedStrategy === 'economical' && 'Most economical option'}
        </Typography>
      </Paper>
    </Box>
  );
};

export default ChargingStrategy;